/**
 * Patient outcomes: colour changes, flips, deterioration, death, release,
 * risk-if-wrong, and route normalisation. (DESIGN §6.6–§6.9)
 */
import { diag } from '../content/compendium.ts';
import type { Colour, Patient, Risk, Role, TableState } from '../types.ts';
import { CONFIRMED } from '../types.ts';
import { clockLength, colourRank, colourUp, diagName, emit, nextStep, unplace, type Ctx } from './common.ts';

/** Change colour, capping the clock one short of the new row (D-04). Returns true if it stepped up. */
export function setColour(p: Patient, c: Colour): boolean {
  const up = colourRank(c) > colourRank(p.colour);
  p.colour = c;
  const len = clockLength(c);
  if (p.ticks >= len) p.ticks = len - 1;
  return up;
}

export function addTicks(p: Patient, n: number): void {
  p.ticks += n;
}

/** Skip route STABILISE steps the patient has already satisfied. */
export function normaliseRoute(p: Patient): void {
  if (!p.route) return;
  for (let guard = 0; guard < 4; guard++) {
    const st = nextStep(p);
    if (st && st.kind === 'STABILISE' && !p.needsStabilise) p.route.next++;
    else break;
  }
}

/** Remove a patient from play-space bookkeeping (bed, open cards, Theatre booking, vent). */
export function release(ctx: Ctx, t: TableState, p: Patient): void {
  if (p.seat) p.flags.lastSeat = p.seat;
  unplace(ctx.s, p);
  t.openCards = t.openCards.filter((o) => o.patient !== p.id);
  if (t.board.theatre.booking?.patient === p.id) t.board.theatre.booking = null;
  p.flags.ventHeld = false;
  p.flags.decree = false;
}

export function reveal(ctx: Ctx, p: Patient, line: Patient['revealed'][number]['line'], text: string): void {
  p.revealed.push({ line, text, turn: ctx.s.clock.turn });
}

/** The patient flips to diagnosis x (a test or a TRUTH read). DESIGN §6.6. */
export function flip(ctx: Ctx, t: TableState, p: Patient, x: string, cause: string): void {
  const before = p.colour;
  const holder = p.seat;
  p.believedKey = x;
  const stepped = setColour(p, diag(x).colour);
  p.box = CONFIRMED;
  p.route = null;
  p.ruledOut = [];
  if (p.colour === 'RED' && colourRank(before) < colourRank('RED')) p.needsStabilise = true;
  const back = ctx.s.backs[p.id];
  if (back) {
    if (back.bias.length) reveal(ctx, p, 'BIAS', `${back.bias[0]}: ${back.bias[1] ?? ''}`.trim());
    if (back.trap && !t.trapList.includes(p.id)) t.trapList.push(p.id);
  }
  emit(ctx, {
    type: 'FLIP', table: t.no, seat: holder, patient: p.id, severity: back?.trap ? 2 : 1,
    tag: back?.bias[0] ?? 'Cognitive bias',
    text: `${p.name}: it's ${diagName(x)}${stepped ? ` — now ${p.colour.toLowerCase()}` : ''} (${cause}).`,
    audience: holder ? ['faculty', `seat:${holder}`] : ['faculty'],
    data: { from: before, to: p.colour, x },
  });
}

/** Green/Amber → one colour worse, Harmed +1, owes a Stabilise. DESIGN §6.8. */
export function deteriorate(ctx: Ctx, t: TableState, p: Patient, cause: string): void {
  const holder = p.seat;
  if (p.colour === 'RED') {
    // A Red cannot step up: harm and owed Stabilise (D-21).
    t.dashboard.harmed++;
    p.needsStabilise = true;
    addTicks(p, 1);
    if (holder) t.seats[holder]!.stats.deteriorationsOnList++;
    emit(ctx, { type: 'HARMED', table: t.no, seat: holder, patient: p.id, severity: 2, tag: 'Outcome', text: `${p.name} (red) harmed: ${cause}.`, audience: holder ? ['faculty', `seat:${holder}`] : ['faculty'] });
    return;
  }
  setColour(p, colourUp(p.colour));
  t.dashboard.harmed++;
  p.needsStabilise = true;
  p.turnScratch.deteriorated = true;
  if (holder) t.seats[holder]!.stats.deteriorationsOnList++;
  emit(ctx, {
    type: 'DETERIORATED', table: t.no, seat: holder, patient: p.id, severity: 2, tag: 'Outcome',
    text: `${p.name} deteriorated to ${p.colour.toLowerCase()}: ${cause}.`, audience: holder ? ['faculty', `seat:${holder}`] : ['faculty'],
    data: { cause },
  });
}

export function die(ctx: Ctx, t: TableState, p: Patient, cause: string): void {
  const holder = p.seat;
  t.dashboard.died++;
  if (holder) {
    const st = t.seats[holder]!;
    st.stats.deathsByTurn[ctx.s.clock.turn] = (st.stats.deathsByTurn[ctx.s.clock.turn] ?? 0) + 1;
  }
  const back = ctx.s.backs[p.id];
  if (back) {
    reveal(ctx, p, 'TRUTH', `TRUTH: ${diagName(back.truthKey)}`);
    if (back.trap && !t.trapList.includes(p.id)) t.trapList.push(p.id);
  }
  release(ctx, t, p);
  p.status = 'DIED';
  emit(ctx, {
    type: 'DIED', table: t.no, seat: holder, patient: p.id, severity: 3, tag: 'Outcome',
    text: `${p.name} died: ${cause}.`, audience: holder ? ['faculty', `seat:${holder}`] : ['faculty'], data: { cause },
  });
}

/** Wrong-diagnosis treatment: flip first, then the risk (D-21). */
export function applyRiskIfWrong(ctx: Ctx, t: TableState, p: Patient, truth: string, risk: Risk, what: string): void {
  flip(ctx, t, p, truth, `the treatment read the truth`);
  switch (risk) {
    case 'NONE':
      emit(ctx, { type: 'WRONG_NONE', table: t.no, seat: p.seat, patient: p.id, severity: 1, tag: 'Probability × risk', text: `${what} on ${p.name} was for the wrong diagnosis: action wasted.`, audience: ['faculty'] });
      break;
    case 'LOW':
      addTicks(p, 1);
      emit(ctx, { type: 'WRONG_LOW', table: t.no, seat: p.seat, patient: p.id, severity: 1, tag: 'Probability × risk', text: `${what} on ${p.name} was for the wrong diagnosis: waiting time +1.`, audience: ['faculty'] });
      break;
    case 'HIGH':
      deteriorate(ctx, t, p, `${what} for the wrong diagnosis`);
      break;
    case 'CRITICAL':
      die(ctx, t, p, `${what} for the wrong diagnosis`);
      break;
  }
}

/** Mark a patient as attended this turn by a seat. */
export function attend(ctx: Ctx, p: Patient, by: Role | 'LOCUM'): void {
  p.attendedTurn = ctx.s.clock.turn;
  if (by !== 'LOCUM') {
    const list = p.turnScratch.actedBy ?? [];
    if (!list.includes(by)) list.push(by);
    p.turnScratch.actedBy = list;
  }
}
